/**
 * WebSocket nativo sin Laravel Echo - más simple y directo
 */
class NativeWebSocket {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 20; // Aumentar intentos
    this.reconnectDelay = 1000; // Delay inicial más bajo
    this.maxReconnectDelay = 30000; // Máximo 30 segundos
    this.heartbeatInterval = null;
    this.lastHeartbeat = null;
    this.connectionState = 'disconnected'; // 'connected', 'connecting', 'disconnected', 'error'
    this.statusCallbacks = [];
    this.connectionStartTime = null;
  }

  connect() {
    // Evitar múltiples conexiones
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) {
      console.log('↩️ WebSocket ya está conectado o conectando');
      return;
    }

    // Limpiar cualquier WebSocket existente
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws = null;
    }

    // USAR WSS porque la página es HTTPS - conectar a través de Nginx proxy
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const host = window.location.hostname; // usar mismo host que la página
    
    // Para HTTPS, usar la ruta del proxy Nginx configurada para WebSocket
    // Según CLAUDE.md, el proxy debe tener configuración para /app/ path
    let wsUrl;
    if (window.location.protocol === 'https:') {
      // En producción HTTPS, conectar vía proxy Nginx
      wsUrl = `${protocol}://${host}/app/mogotax-app-key?protocol=7&client=js&version=1.0.0`;
    } else {
      // En desarrollo HTTP, conectar directamente al puerto 8082
      wsUrl = `${protocol}://${host}:8082/app/mogotax-app-key?protocol=7&client=js&version=1.0.0`;
    }
    
    this.updateConnectionState('connecting');
    this.connectionStartTime = Date.now();

    try {
      this.ws = new WebSocket(wsUrl);
      let connectionTimeout = null;
      
      // Timeout para conexión inicial
      connectionTimeout = setTimeout(() => {
        if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
          console.warn('⚠️ Timeout de conexión WebSocket');
          this.ws.close();
        }
      }, 15000); // 15 segundos para conectar
      
      this.ws.onopen = (event) => {
        clearTimeout(connectionTimeout);
        const connectionTime = Date.now() - this.connectionStartTime;
        console.log(`✅ WebSocket conectado en ${connectionTime}ms`);
        
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000; // Reset delay
        this.updateConnectionState('connected');
        
        // Suscribirse a los canales
        this.subscribe('traslados');
        this.subscribe('control-servicios');

        // Iniciar heartbeat
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Verificar si es un evento de traslado
          if (data.event === 'traslado.simple.update') {
            this.handleTrasladoUpdate(data.data);
          } else if (data.event === 'traslado.simple.created') {
            this.handleTrasladoCreated(data.data);
          } else if (data.event === 'traslado_estado_cambiado') {
            this.handleTrasladoStatusChange(data.data);
          } else if (data.event === 'traslado.actualizado') {
            this.handleTrasladoActualizado(data.data);
          } else if (data.event === 'observaciones.updated') {
            this.handleObservacionesUpdate(data.data);
          }
          // Eventos de Control de Servicios
          else if (data.event === 'control_servicio.created') {
            this.handleControlServicioCreated(data.data);
          } else if (data.event === 'control_servicio.updated') {
            this.handleControlServicioUpdated(data.data);
          } else if (data.event === 'control_servicio.tag_changed') {
            this.handleControlServicioTagChanged(data.data);
          } else if (data.event === 'control_servicio.estado_changed') {
            this.handleControlServicioEstadoChanged(data.data);
          } else if (data.event === 'control_servicio.novedad_added') {
            this.handleControlServicioNovedadAdded(data.data);
          } else if (data.event === 'control_servicio.validated') {
            this.handleControlServicioValidated(data.data);
          } else if (data.event === 'control_servicio.rejected') {
            this.handleControlServicioRejected(data.data);
          }
        } catch (e) {
          console.warn('⚠️ Error parseando mensaje WebSocket:', e);
        }
      };

      this.ws.onclose = (event) => {
        this.updateConnectionState('disconnected');
        this.stopHeartbeat();
        this.attemptReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('❌ WebSocket ERROR:', error);
        this.updateConnectionState('error');
      };

    } catch (error) {
      console.error('❌ Error creando WebSocket:', error);
      this.attemptReconnect();
    }
  }

  subscribe(channel) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const subscribeMessage = {
        event: 'pusher:subscribe',
        data: {
          channel: channel
        }
      };
      
      console.log(`📢 Suscribiéndose al canal: ${channel}`);
      this.ws.send(JSON.stringify(subscribeMessage));
    } else {
      console.warn(`⚠️ No se pudo suscribir al canal ${channel}, WebSocket no está abierto`);
    }
  }

  handleTrasladoUpdate(data) {
    // Parsear datos si vienen como string
    let parsedData = data;
    if (typeof data === 'string') {
      try {
        parsedData = JSON.parse(data);
      } catch (e) {
        console.error('Error parseando datos de traslado:', e);
        return;
      }
    }
    
    // Llamar callbacks registrados
    const callbacks = this.listeners.get('traslado.update') || [];
    callbacks.forEach(callback => {
      try {
        callback(parsedData);
      } catch (e) {
        console.error('Error en callback:', e);
      }
    });
  }

  handleTrasladoCreated(data) {
    // Parsear datos si vienen como string
    let parsedData = data;
    if (typeof data === 'string') {
      try {
        parsedData = JSON.parse(data);
      } catch (e) {
        console.error('Error parseando datos de traslado creado:', e);
        return;
      }
    }
    
    // Llamar callbacks registrados para creación
    const callbacks = this.listeners.get('traslado.created') || [];
    callbacks.forEach(callback => {
      try {
        callback(parsedData);
      } catch (e) {
        console.error('Error en callback de creación:', e);
      }
    });
  }

  handleTrasladoStatusChange(data) {
    // Parsear datos si vienen como string
    let parsedData = data;
    if (typeof data === 'string') {
      try {
        parsedData = JSON.parse(data);
      } catch (e) {
        console.error('Error parseando datos de cambio de estado:', e);
        return;
      }
    }
    
    // El evento contiene el traslado completo actualizado
    const { trasladoId, traslado } = parsedData;
    
    if (!trasladoId || !traslado) {
      console.warn('⚠️ Datos incompletos en cambio de estado:', parsedData);
      return;
    }
    
    // Llamar callbacks registrados para actualización de estado
    const callbacks = this.listeners.get('traslado.status.change') || [];
    callbacks.forEach(callback => {
      try {
        callback({ trasladoId, traslado });
      } catch (e) {
        console.error('Error en callback de cambio de estado:', e);
      }
    });
  }

  handleTrasladoActualizado(data) {
    // Parsear datos si vienen como string
    let parsedData = data;
    if (typeof data === 'string') {
      try {
        parsedData = JSON.parse(data);
      } catch (e) {
        console.error('Error parseando datos de traslado actualizado:', e);
        return;
      }
    }
    
    // El evento contiene el traslado completo actualizado
    const { trasladoId, traslado, changes } = parsedData;
    
    if (!trasladoId || !traslado) {
      console.warn('⚠️ Datos incompletos en actualización:', parsedData);
      return;
    }
    
    // Llamar callbacks registrados para actualización completa
    const callbacks = this.listeners.get('traslado.actualizado') || [];
    callbacks.forEach(callback => {
      try {
        callback({ trasladoId, traslado, changes });
      } catch (e) {
        console.error('Error en callback de actualización:', e);
      }
    });
  }

  handleObservacionesUpdate(data) {
    // Parsear datos si vienen como string
    let parsedData = data;
    if (typeof data === 'string') {
      try {
        parsedData = JSON.parse(data);
      } catch (e) {
        console.error('Error parseando datos de observaciones:', e);
        return;
      }
    }
    
    // El evento contiene el traslado_id y el nuevo contador
    const { trasladoId, observaciones_count } = parsedData;
    
    if (!trasladoId || observaciones_count === undefined) {
      console.warn('⚠️ Datos incompletos en actualización de observaciones:', parsedData);
      return;
    }
    
    // Llamar callbacks registrados para actualización de observaciones
    const callbacks = this.listeners.get('observaciones.updated') || [];
    callbacks.forEach(callback => {
      try {
        callback({ trasladoId, observaciones_count });
      } catch (e) {
        console.error('Error en callback de observaciones:', e);
      }
    });
  }

  // ==========================================
  // Control de Servicios Event Handlers
  // ==========================================

  handleControlServicioCreated(data) {
    let parsedData = data;
    if (typeof data === 'string') {
      try {
        parsedData = JSON.parse(data);
      } catch (e) {
        console.error('Error parseando datos de control servicio creado:', e);
        return;
      }
    }

    const callbacks = this.listeners.get('control_servicio.created') || [];
    callbacks.forEach(callback => {
      try {
        callback(parsedData);
      } catch (e) {
        console.error('Error en callback de control servicio creado:', e);
      }
    });
  }

  handleControlServicioUpdated(data) {
    let parsedData = data;
    if (typeof data === 'string') {
      try {
        parsedData = JSON.parse(data);
      } catch (e) {
        console.error('Error parseando datos de control servicio actualizado:', e);
        return;
      }
    }

    const callbacks = this.listeners.get('control_servicio.updated') || [];
    callbacks.forEach(callback => {
      try {
        callback(parsedData);
      } catch (e) {
        console.error('Error en callback de control servicio actualizado:', e);
      }
    });
  }

  handleControlServicioTagChanged(data) {
    let parsedData = data;
    if (typeof data === 'string') {
      try {
        parsedData = JSON.parse(data);
      } catch (e) {
        console.error('Error parseando datos de cambio de tag:', e);
        return;
      }
    }

    const callbacks = this.listeners.get('control_servicio.tag_changed') || [];
    callbacks.forEach(callback => {
      try {
        callback(parsedData);
      } catch (e) {
        console.error('Error en callback de cambio de tag:', e);
      }
    });
  }

  handleControlServicioEstadoChanged(data) {
    let parsedData = data;
    if (typeof data === 'string') {
      try {
        parsedData = JSON.parse(data);
      } catch (e) {
        console.error('Error parseando datos de cambio de estado:', e);
        return;
      }
    }

    const callbacks = this.listeners.get('control_servicio.estado_changed') || [];
    callbacks.forEach(callback => {
      try {
        callback(parsedData);
      } catch (e) {
        console.error('Error en callback de cambio de estado:', e);
      }
    });
  }

  handleControlServicioNovedadAdded(data) {
    let parsedData = data;
    if (typeof data === 'string') {
      try {
        parsedData = JSON.parse(data);
      } catch (e) {
        console.error('Error parseando datos de novedad agregada:', e);
        return;
      }
    }

    const callbacks = this.listeners.get('control_servicio.novedad_added') || [];
    callbacks.forEach(callback => {
      try {
        callback(parsedData);
      } catch (e) {
        console.error('Error en callback de novedad agregada:', e);
      }
    });
  }

  handleControlServicioValidated(data) {
    let parsedData = data;
    if (typeof data === 'string') {
      try {
        parsedData = JSON.parse(data);
      } catch (e) {
        console.error('Error parseando datos de validación:', e);
        return;
      }
    }

    const callbacks = this.listeners.get('control_servicio.validated') || [];
    callbacks.forEach(callback => {
      try {
        callback(parsedData);
      } catch (e) {
        console.error('Error en callback de validación:', e);
      }
    });
  }

  handleControlServicioRejected(data) {
    let parsedData = data;
    if (typeof data === 'string') {
      try {
        parsedData = JSON.parse(data);
      } catch (e) {
        console.error('Error parseando datos de rechazo:', e);
        return;
      }
    }

    const callbacks = this.listeners.get('control_servicio.rejected') || [];
    callbacks.forEach(callback => {
      try {
        callback(parsedData);
      } catch (e) {
        console.error('Error en callback de rechazo:', e);
      }
    });
  }

  onTrasladoUpdate(callback) {
    if (!this.listeners.has('traslado.update')) {
      this.listeners.set('traslado.update', []);
    }
    this.listeners.get('traslado.update').push(callback);
  }

  onTrasladoCreated(callback) {
    if (!this.listeners.has('traslado.created')) {
      this.listeners.set('traslado.created', []);
    }
    this.listeners.get('traslado.created').push(callback);
  }

  onTrasladoStatusChange(callback) {
    if (!this.listeners.has('traslado.status.change')) {
      this.listeners.set('traslado.status.change', []);
    }
    this.listeners.get('traslado.status.change').push(callback);
  }

  onTrasladoActualizado(callback) {
    if (!this.listeners.has('traslado.actualizado')) {
      this.listeners.set('traslado.actualizado', []);
    }
    this.listeners.get('traslado.actualizado').push(callback);
  }

  onObservacionesUpdate(callback) {
    if (!this.listeners.has('observaciones.updated')) {
      this.listeners.set('observaciones.updated', []);
    }
    this.listeners.get('observaciones.updated').push(callback);
  }

  // ==========================================
  // Método genérico para remover listeners
  // ==========================================

  removeListener(event, callback) {
    if (this.listeners.has(event)) {
      const listeners = this.listeners.get(event);
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  // ==========================================
  // Control de Servicios Listener Registration
  // ==========================================

  onControlServicioCreated(callback) {
    if (!this.listeners.has('control_servicio.created')) {
      this.listeners.set('control_servicio.created', []);
    }
    this.listeners.get('control_servicio.created').push(callback);
  }

  onControlServicioUpdated(callback) {
    if (!this.listeners.has('control_servicio.updated')) {
      this.listeners.set('control_servicio.updated', []);
    }
    this.listeners.get('control_servicio.updated').push(callback);
  }

  onControlServicioTagChanged(callback) {
    if (!this.listeners.has('control_servicio.tag_changed')) {
      this.listeners.set('control_servicio.tag_changed', []);
    }
    this.listeners.get('control_servicio.tag_changed').push(callback);
  }

  onControlServicioEstadoChanged(callback) {
    if (!this.listeners.has('control_servicio.estado_changed')) {
      this.listeners.set('control_servicio.estado_changed', []);
    }
    this.listeners.get('control_servicio.estado_changed').push(callback);
  }

  onControlServicioNovedadAdded(callback) {
    if (!this.listeners.has('control_servicio.novedad_added')) {
      this.listeners.set('control_servicio.novedad_added', []);
    }
    this.listeners.get('control_servicio.novedad_added').push(callback);
  }

  onControlServicioValidated(callback) {
    if (!this.listeners.has('control_servicio.validated')) {
      this.listeners.set('control_servicio.validated', []);
    }
    this.listeners.get('control_servicio.validated').push(callback);
  }

  onControlServicioRejected(callback) {
    if (!this.listeners.has('control_servicio.rejected')) {
      this.listeners.set('control_servicio.rejected', []);
    }
    this.listeners.get('control_servicio.rejected').push(callback);
  }

  startHeartbeat() {
    // Enviar ping cada 25 segundos (menos que el timeout del servidor)
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        const pingMessage = {
          event: 'pusher:ping',
          data: {}
        };
        
        try {
          this.ws.send(JSON.stringify(pingMessage));
          this.lastHeartbeat = Date.now();
        } catch (error) {
          console.error('❌ Error enviando ping:', error);
          // Si falla el ping, cerrar y reconectar
          this.ws.close();
        }
      }
    }, 25000); // 25 segundos (menos que el ping interval del servidor)
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      
      // Backoff exponencial con jitter
      const baseDelay = Math.min(this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts - 1), this.maxReconnectDelay);
      const jitter = Math.random() * 1000; // 0-1 segundo de jitter
      const delay = Math.floor(baseDelay + jitter);
      
      console.log(`🔄 Reintento ${this.reconnectAttempts}/${this.maxReconnectAttempts} en ${delay}ms`);
      
      setTimeout(() => {
        this.connect();
      }, delay);
    } else {
      console.error('❌ Máximo de reintentos alcanzado');
      this.updateConnectionState('error');
    }
  }

  disconnect() {
    this.stopHeartbeat();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.updateConnectionState('disconnected');
  }

  updateConnectionState(newState) {
    this.connectionState = newState;
    this.statusCallbacks.forEach(callback => {
      try {
        callback(newState);
      } catch (e) {
        console.error('Error en callback de estado:', e);
      }
    });
  }

  onStatusChange(callback) {
    // Evitar duplicados
    if (!this.statusCallbacks.includes(callback)) {
      this.statusCallbacks.push(callback);
    }
    // Llamar inmediatamente con el estado actual
    callback(this.connectionState);
  }

  getConnectionState() {
    return this.connectionState;
  }

  isConnected() {
    return this.connectionState === 'connected';
  }
}

// Crear instancia global
const nativeWebSocket = new NativeWebSocket();

export default nativeWebSocket;